import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { CONTRACT_CONSTANTS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { formatFloat } from '../../../utils/numberUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import TextInputField from '../../formElements/TextInputField';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateCoursesAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const acceptedRoles = [SUPERUSER];
const _MESSAGE = 'Contract constants has been updated';

const CoursesAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        selfEmployedCoachPaymentRate: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!contractId) return;
        setIsDisabled(!hasRole(acceptedRoles, roles));
        if (
            Object.keys(contractConstants)?.length < 1 ||
            ('contractId' in contractConstants && contractConstants.contractId !== contractId)
        )
            dispatch(loadContractConstants(contractId));
    }, [contractId, roles]);

    useEffect(() => {
        if (Object.keys(contractConstants).length && contractConstants.contractId === contractId) {
            setNewEntry((prev) => ({
                ...prev,
                selfEmployedCoachPaymentRate: contractConstants.selfEmployedCoachPaymentRate
            }));
        }
    }, [contractConstants]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (!Object.keys(contractConstants).length) return CONTRACT_CONSTANTS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdateSelfEmployedCoachPaymentRate = (e) => {
        clearError('selfEmployedCoachPaymentRate');
        setNewEntry((prev) => ({
            ...prev,
            selfEmployedCoachPaymentRate: formatFloat(e.target.value)
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, contractConstants);
        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateContractConstants({ ...contractConstants, ...newEntry }, _MESSAGE));
    };

    // AWAITING CONTENT
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <form className={form.formWrapper} onSubmit={onSubmit} data-testid="admin_courses_form">
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <TextInputField
                        id="selfEmployedCoachPaymentRate"
                        label="Self Employed Coach Payment Rate"
                        placeholder="Enter payment rate..."
                        disabled={isDisabled}
                        maxLength={6}
                        value={newEntry.selfEmployedCoachPaymentRate || ''}
                        error={errors.selfEmployedCoachPaymentRate}
                        onChange={onUpdateSelfEmployedCoachPaymentRate}
                    />
                </div>
                <div className={form.formColumn}></div>
            </div>

            <Button
                id="coursesAdmin"
                content="UPDATE COURSES"
                disabled={isDisabled || isButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

CoursesAdmin.propTypes = {
    contractId: PropTypes.string
};

export default CoursesAdmin;
